/**
 * WebSocket Integration Hook
 *
 * Provides a centralized hook for WebSocket notifications that integrates with:
 * - Current terreiro from localStorage
 * - Current user from auth context
 * - Request notifications via useRequestNotifications
 *
 * This hook is the main entry point for components that need real-time notifications.
 */
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import { useAuthContext } from "../auth/AuthContext";
import { ROUTE } from "../constants/routes";
import { resolveWebSocketScope } from "../utils/websocketScope";
import {
  WSMessageType,
  type WSConnectionState,
} from "../types/websocket.types";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { useHasPermission } from "./useHasPermission";
import {
  useGamificationRealtimeSync,
  type GamificationBadgeEarned,
  type GamificationMilestoneReached,
  type GamificationRankChange,
  type GamificationStreakUpdated,
} from "./useGamificationRealtimeSync";
import { notificationQueryKeys } from "./useNotificationsQuery";
import {
  socialQueryKeys,
  type CursorPage,
} from "../repositories/socialRepository";
import type { CachedSocialMessage } from "./useSocialMessagingQueries";
import type { RequestNotification } from "./useRequestNotifications";
import { useRequestNotifications } from "./useRequestNotifications";
import {
  useWebSocket,
  useWebSocketConnection,
  useWebSocketMessage,
} from "./useWebSocket";

export interface UseWebSocketIntegrationOptions {
  /** Whether to enable WebSocket connection */
  enabled?: boolean;
  /** Callback when a new notification arrives */
  onNotification?: (notification: RequestNotification) => void;
  /** Maximum notifications to keep in memory */
  maxNotifications?: number;
  /** Callback when current medium ranking changes */
  onRankChange?: (change: GamificationRankChange) => void;
  /** Callback when current medium earns a new badge */
  onBadgeEarned?: (badge: GamificationBadgeEarned) => void;
  /** Callback when current medium streak changes */
  onStreakUpdated?: (streak: GamificationStreakUpdated) => void;
  /** Callback when current medium reaches a points milestone */
  onMilestoneReached?: (milestone: GamificationMilestoneReached) => void;
}

export interface UseWebSocketIntegrationReturn {
  /** All request notifications */
  notifications: RequestNotification[];
  /** Count of unread notifications */
  unreadCount: number;
  /** Latest notification (for toast display) */
  latestNotification: RequestNotification | null;
  /** WebSocket connection state */
  connectionState: WSConnectionState;
  /** Whether connected */
  isConnected: boolean;
  /** Current reconnection attempt count */
  reconnectAttempts: number;
  /** Mark a notification as read */
  markAsRead: (notificationId: string) => void;
  /** Mark all notifications as read */
  markAllAsRead: () => void;
  /** Clear all notifications */
  clearAll: () => void;
  /** Navigate to a specific notification's related content */
  navigateToNotification: (notification: RequestNotification) => void;
  /** Navigate to the notifications page */
  navigateToNotifications: () => void;
  /** Navigate to the unread notifications view */
  navigateToUnreadNotifications: () => void;
}

/**
 * Main integration hook for WebSocket notifications.
 * Automatically connects to WebSocket when user is authenticated and has a selected terreiro.
 */
export function useWebSocketIntegration(
  options: UseWebSocketIntegrationOptions = {}
): UseWebSocketIntegrationReturn {
  const {
    enabled = true,
    onNotification,
    maxNotifications = 50,
    onRankChange,
    onBadgeEarned,
    onStreakUpdated,
    onMilestoneReached,
  } = options;

  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuthContext();
  const cachedTerreiro = useCachedTerreiro();
  const cachedMedium = useCachedMedium();

  const terreiroId = cachedTerreiro?.id;
  const mediumId = cachedMedium?.id;
  const userId = (user as Record<string, unknown>)?.id as string | undefined;
  const isSocialMessagingRoute = location.pathname.startsWith(
    ROUTE.SOCIAL_MESSAGES
  );

  // Terreiro-scoped permission check (replaces legacy isAdmin())
  const isAdminPermission = useHasPermission(terreiroId, "member.approve");

  // Determine if we should connect
  const shouldConnect =
    enabled &&
    isAuthenticated &&
    (isSocialMessagingRoute || !!terreiroId || !!mediumId);

  // Connect to WebSocket
  useWebSocket({
    ...resolveWebSocketScope({
      terreiroId,
      mediumId,
      scope: isSocialMessagingRoute ? "social" : undefined,
    }),
    enabled: shouldConnect,
    debug: import.meta.env.DEV,
  });

  // Get connection state
  const {
    state: connectionState,
    isConnected,
    reconnectAttempts,
  } = useWebSocketConnection();

  // Use request notifications hook
  const {
    notifications,
    unreadCount,
    latestNotification,
    markAsRead,
    markAllAsRead,
    clearAll,
  } = useRequestNotifications({
    userId,
    mediumId,
    terreiroId,
    isAdmin: isAdminPermission,
    maxNotifications,
    onNotification,
  });

  useGamificationRealtimeSync({
    terreiroId,
    mediumId,
    enabled: shouldConnect,
    onRankChange,
    onBadgeEarned,
    onStreakUpdated,
    onMilestoneReached,
  });

  // Listen for general notification.created WS events and invalidate the notifications cache
  const queryClient = useQueryClient();
  useWebSocketMessage(WSMessageType.NOTIFICATION_CREATED, (message) => {
    const payload = message.payload as { terreiroId?: string } | undefined;
    const payloadTerreiroId = payload?.terreiroId;

    if (terreiroId && payloadTerreiroId && payloadTerreiroId !== terreiroId) {
      return;
    }

    if (!terreiroId && mediumId && payloadTerreiroId) {
      return;
    }

    queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all });
  });

  useWebSocketMessage(WSMessageType.SOCIAL_MESSAGE_CREATED, (message) => {
    if (!isSocialMessagingRoute) return;
    const payload = message.payload as
      | { conversationId?: string; messageId?: string }
      | undefined;
    if (!payload?.conversationId || !payload.messageId) return;

    queryClient.invalidateQueries({
      queryKey: socialQueryKeys.messages(payload.conversationId),
      exact: true,
    });
    queryClient.invalidateQueries({
      queryKey: socialQueryKeys.conversations(),
      exact: true,
    });
  });

  useWebSocketMessage(WSMessageType.SOCIAL_CONVERSATION_READ, (message) => {
    if (!isSocialMessagingRoute) return;
    const payload = message.payload as
      | { conversationId?: string; readAt?: string }
      | undefined;
    if (!payload?.conversationId || !payload.readAt) return;
    const readAt = Date.parse(payload.readAt);

    queryClient.setQueryData<
      InfiniteData<CursorPage<CachedSocialMessage>, string | undefined>
    >(socialQueryKeys.messages(payload.conversationId), (current) =>
      current
        ? {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              items: page.items.map((item) =>
                item.senderId === userId && Date.parse(item.createdAt) <= readAt
                  ? { ...item, deliveryStatus: "READ" }
                  : item
              ),
            })),
          }
        : current
    );
  });

  // Navigate to notification's related content
  const navigateToNotification = useCallback(
    (notification: RequestNotification) => {
      const { requestId, requestType } = notification;

      // Navigate based on request type
      // For now, go to the general requests/notifications page
      // In the future, we can add specific routes for different request types
      switch (requestType) {
        case "ACTIVITY_PARTICIPATION":
          navigate(`${ROUTE.ACTIVITIES}?highlight=${requestId}`);
          break;
        case "EVENT_PARTICIPATION":
          navigate(`${ROUTE.EVENTS}?highlight=${requestId}`);
          break;
        case "RITUAL_BATH":
          navigate(`${ROUTE.RITUAL_BATHS}?highlight=${requestId}`);
          break;
        default:
          navigate(ROUTE.NOTIFICATIONS);
      }

      // Mark as read
      markAsRead(notification.id);
    },
    [navigate, markAsRead]
  );

  // Navigate to notifications page
  const navigateToNotifications = useCallback(() => {
    navigate(ROUTE.NOTIFICATIONS);
  }, [navigate]);

  const navigateToUnreadNotifications = useCallback(() => {
    navigate(`${ROUTE.NOTIFICATIONS}?read=unread`);
  }, [navigate]);

  return useMemo(
    () => ({
      notifications,
      unreadCount,
      latestNotification,
      connectionState,
      isConnected,
      reconnectAttempts,
      markAsRead,
      markAllAsRead,
      clearAll,
      navigateToNotification,
      navigateToNotifications,
      navigateToUnreadNotifications,
    }),
    [
      notifications,
      unreadCount,
      latestNotification,
      connectionState,
      isConnected,
      reconnectAttempts,
      markAsRead,
      markAllAsRead,
      clearAll,
      navigateToNotification,
      navigateToNotifications,
      navigateToUnreadNotifications,
    ]
  );
}

export default useWebSocketIntegration;
